import { createStore } from "@stencil/store";
import { Wishlist, WishlistEntry } from "api/model/wishlist";
import {
  baseSiteId,
  userId,
  wishlistsApi,
} from "frontend-kit/organisms/f-header/f-wishlist-flyout/f-wishlist-config";
import GetWishlistsResponse from "@apis/responses/get-wishlists-response";
import { ErrorResponseWrapper } from "@apis/errors/error-response-wrapper";
import { countWishlistEntries } from "frontend-kit/organisms/f-header/f-wishlist-flyout/f-wishlist-service";
import { RequestHeaderService } from "@globals/helpers";

function addProductToWishlist(wishlistCode: string, product: WishlistEntry) {
  const newWishlists = state.wishlists.map((wishlist) => {
    if (wishlist.code === wishlistCode) {
      wishlist.entries = [product, ...wishlist.entries];
      wishlist.totalPrice = product.totalWishlistPrice;
    }
    return wishlist;
  });
  setWishlists(newWishlists);
}

export const setWishlists = (wishlistsToSet: Wishlist[]) => {
  state.wishlists = wishlistsToSet;
  state.numberOfWishlistsItems = countWishlistEntries(state.wishlists);
  set("is", "LOADED");
};

export async function getWishlists(): Promise<void> {
  const url = `${wishlistsApi}/${baseSiteId}/users/${userId}/wishlists`;
  const headers = RequestHeaderService.getDefaultHeaders();
  set("is", "LOADING");
  return fetch(url, {
    method: "GET",
    ...headers,
  }).then(async (response) => {
    if (!response.ok) {
      handleErrors();
      throw new ErrorResponseWrapper(response);
    }

    const wishlistsResponse: GetWishlistsResponse = await response.json();
    setWishlists(wishlistsResponse.wishlists);
  });
}

export async function addEntryToWishlist(
  wishlistCode: string,
  sku: string,
  quantity: number
): Promise<void> {
  const url = `${wishlistsApi}/${baseSiteId}/users/${userId}/wishlists/${wishlistCode}/entries`;
  const headers = RequestHeaderService.getDefaultHeaders();
  set("is", "LOADING");
  return fetch(url, {
    method: "POST",
    ...headers,
    body: JSON.stringify({
      sku,
      quantity,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      handleErrors();
      throw new ErrorResponseWrapper(response);
    }

    const addedProduct: WishlistEntry = await response.json();
    addProductToWishlist(wishlistCode, addedProduct);
    set("is", "LOADED");
  });
}

export async function addEntryToNewWishlist(
  sku: string,
  quantity: number
): Promise<Wishlist> {
  const url = `${wishlistsApi}/${baseSiteId}/users/${userId}/wishlists`;
  const headers = RequestHeaderService.getDefaultHeaders();
  set("is", "LOADING");
  return fetch(url, {
    method: "POST",
    ...headers,
    body: JSON.stringify({
      sku,
      quantity,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      handleErrors();
      throw new ErrorResponseWrapper(response);
    }

    const createdWishlist: Wishlist = await response.json();
    setWishlists([...get("wishlists"), createdWishlist]);
    set("is", "LOADED");
    return createdWishlist;
  });
}

interface WishlistStore {
  numberOfWishlistsItems: number;
  wishlists: Wishlist[];
  is: "LOADING" | "LOADED" | "ERROR";
}

const { state, set, get } = createStore({
  numberOfWishlistsItems: 0,
  wishlists: [],
} as WishlistStore);

export const handleErrors = () => {
  set("is", "ERROR");
};

export { get, set };
export default state;
